import React, { useEffect, useState } from 'react';
import { usePage } from '@inertiajs/react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

interface RootLayoutProps {
  children: React.ReactNode;
}

function IntercomBooter({ children }: { children: React.ReactNode }) {
  const { auth, intercom_app_id } = usePage().props as any;
  const { boot, shutdown, update } = useIntercom();
  
  useEffect(() => {
    if (intercom_app_id && intercom_app_id !== 'YOUR_INTERCOM_APP_ID') {
      // Boot Intercom immediately when the provider loads
      // Since the provider itself is lazy loaded, this is safe
      if (auth?.user) {
        // Boot Intercom with user data for logged-in users
        boot({
          userId: auth.user.id.toString(),
          email: auth.user.email,
          name: `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.email,
          customAttributes: {
            is_admin: auth.user.is_admin || false,
            user_type: 'registered'
          },
          // Force light theme
          theme: 'light'
        });
      } else {
        // Boot Intercom for anonymous visitors
        boot({
          customAttributes: {
            user_type: 'visitor'
          },
          // Force light theme
          theme: 'light'
        });
      }
    }
    
    return () => {
      // Shutdown on unmount
      shutdown();
    };
  }, [auth?.user, boot, shutdown, intercom_app_id]);
  
  // Update Intercom when user data changes
  useEffect(() => {
    if (auth?.user && intercom_app_id && intercom_app_id !== 'YOUR_INTERCOM_APP_ID') {
      update({
        userId: auth.user.id.toString(),
        email: auth.user.email,
        name: `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.email,
        customAttributes: {
          is_admin: auth.user.is_admin || false,
          user_type: 'registered'
        }
      });
    }
  }, [auth?.user, update, intercom_app_id]);
  
  return <>{children}</>;
}

export default function RootLayout({ children }: RootLayoutProps) {
  const { intercom_app_id } = usePage().props as any;
  const [shouldLoadIntercom, setShouldLoadIntercom] = useState(false);
  
  useEffect(() => {
    // Only set up listeners if we have a valid Intercom app ID
    if (!intercom_app_id || intercom_app_id === 'YOUR_INTERCOM_APP_ID') {
      return;
    }
    
    let timeoutId: NodeJS.Timeout;
    let hasTriggered = false;
    
    const triggerIntercomLoad = () => {
      if (!hasTriggered) {
        hasTriggered = true;
        setShouldLoadIntercom(true);
        // Clean up all listeners
        window.removeEventListener('scroll', triggerIntercomLoad);
        window.removeEventListener('click', triggerIntercomLoad);
        window.removeEventListener('touchstart', triggerIntercomLoad);
        window.removeEventListener('mousemove', triggerIntercomLoad);
        if (timeoutId) clearTimeout(timeoutId);
      }
    };
    
    // Load Intercom on any user interaction
    window.addEventListener('scroll', triggerIntercomLoad, { once: true, passive: true });
    window.addEventListener('click', triggerIntercomLoad, { once: true });
    window.addEventListener('touchstart', triggerIntercomLoad, { once: true, passive: true });
    window.addEventListener('mousemove', triggerIntercomLoad, { once: true, passive: true });
    
    // Also load after 4 seconds as a fallback
    timeoutId = setTimeout(triggerIntercomLoad, 4000);
    
    return () => {
      window.removeEventListener('scroll', triggerIntercomLoad);
      window.removeEventListener('click', triggerIntercomLoad);
      window.removeEventListener('touchstart', triggerIntercomLoad);
      window.removeEventListener('mousemove', triggerIntercomLoad);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [intercom_app_id]);
  
  // Don't load Intercom at all if no valid app ID
  if (!intercom_app_id || intercom_app_id === 'YOUR_INTERCOM_APP_ID') {
    return <>{children}</>;
  }
  
  // Use shouldInitialize to control script loading without changing component structure
  // This prevents re-mounting of children when Intercom loads
  return (
    <IntercomProvider 
      appId={intercom_app_id} 
      autoBoot={false}
      shouldInitialize={shouldLoadIntercom} // This controls script loading without changing tree structure
      initializeDelay={0}
    >
      <IntercomBooter>
        {children}
      </IntercomBooter>
    </IntercomProvider>
  );
}